import React from 'react';
import { makeStyles } from '@mui/styles';

const data = [
  {
    title: 'Reword',
    text: 'Ask yourself if any of the words or phrases you used were copied too closely from the original text, and replace anything that is too similar in your paraphrase. It’s especially important to do this for longer, less commonly used words.',
  },
  {
    title: 'Rearrange',
    text: "Changing a sentence's structure can often radically alter its tone, style, and clarity. Try varying the location of clauses, such as testing them at both the beginning and end of a sentence, to see more ways to make your writing more interesting and clear.",
  },
  {
    title: 'Realize',
    text: 'Some words can’t be changed. If there’s specific, unchangeable information (such as a city name or a date), you’ll need to work around that and perhaps use quotations. In these cases, it’s extremely important to focus on originality to avoid unintentional plagiarism.',
  },
  {
    title: 'Recheck',
    text: 'Once you’ve taken note of these steps, it’s time to check it all again. There’s no harm in doing a few more reviews or revisions of your writing to mesh your ideas with those you’re paraphrasing and citing.',
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '60px 15px 60px',
    maxWidth: 665,
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      padding: '60px 32px 60px',
    },
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 20,
  },
  sectionText: {
    fontSize: 16,
  },
  cardsList: {
    marginTop: 40,
    borderRadius: '20px',
    overflow: 'hidden',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    [theme.breakpoints.down('sm')]: {
      margin: '40px -16px 0',
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    display: 'flex',
    minHeight: '186px',
    padding: '12px 32px 36px 12px',
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      borderLeft: '24px solid black',
      borderTop: '32px solid transparent',
      borderBottom: '32px solid transparent',
      zIndex: 1,
    },
    '&:first-child': {
      backgroundColor: '#CAFCD4',
      order: 1,
      // zIndex: 3,
      '&:before': {
        top: '50%',
        right: -22,
        borderLeftColor: '#CAFCD4',
        transform: 'translateY(-50%) rotate(0deg)',
      },
      [theme.breakpoints.down('sm')]: {
        '&:before': {
          top: 'calc(100% - 28px)',
          left: '50%',
          right: 'auto',
          transform: 'translateX(-50%) rotate(90deg)',
        },
      },
    },
    '&:nth-child(2)': {
      backgroundColor: '#8FE39E',
      order: 2,
      // zIndex: 2,
      '&:before': {
        left: '50%',
        top: 'calc(100% - 22px)',
        borderLeftColor: '#8FE39E',
        transform: 'translateX(-50%) rotate(90deg)',
      },
      [theme.breakpoints.down('sm')]: {
        '&:before': {
          top: 'calc(100% - 28px)',
        },
      },
    },
    '&:nth-child(3)': {
      backgroundColor: '#61C774',
      order: 4,
      // zIndex: 1,
      '&:before': {
        top: '50%',
        left: -22,
        borderLeftColor: '#61C774',
        transform: 'translateY(-50%) rotate(180deg)',
      },
      [theme.breakpoints.down('sm')]: {
        order: 3,
        '&:before': {
          top: 'calc(100% - 28px)',
          left: '50%',
          transform: 'translateX(-50%) rotate(90deg)',
        },
      },
    },
    '&:last-child': {
      backgroundColor: '#44A656',
      order: 3,
      // zIndex: 4,
      '&:before': {
        top: '-11px',
        left: '50%',
        borderLeftColor: '#44A656',
        transform: 'translateY(-50%) rotate(-90deg)',
      },
      [theme.breakpoints.down('sm')]: {
        order: 4,
        '&:before': {
          display: 'none',
        },
      },
    },
  },
  cardNumberWrapper: {
    marginRight: 14,
  },
  cardNumber: {
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1,
    width: 37,
    height: 37,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `3px solid ${theme.palette.text.primary}`,
    borderRadius: '50%',
  },
  cardContent: {},
  cardTitle: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1,
    marginBottom: 14,
    marginTop: 8,
  },
  cardText: {
    fontSize: 12,
    lineHeight: '16px',
  },
}));

function FourRs() {
  const classes = useStyles();

  return (
    <>
      <div className={classes.root}>
        <div className={classes.sectionTitle}>The Four R’s of Paraphrasing</div>
        <div className={classes.sectionText}>
          <p>
            To improve a paraphrase, be sure to review both your writing and the reference material
            multiple times. Look for any ways to make the paraphrase more independent from the
            original text and more geared toward supporting your unique ideas with credible outside
            support.
          </p>
          <p>To do this, follow the “Four R’s of Paraphrasing”:</p>
        </div>
        <div className={classes.cardsList}>
          {data.map((item, index) => (
            <RCard key={item.title} title={item.title} text={item.text} number={index + 1} />
          ))}
        </div>
      </div>
    </>
  );
}

export default FourRs;

function RCard({ title, text, number }: any) {
  const classes = useStyles();

  return (
    <div className={classes.card}>
      <div className={classes.cardNumberWrapper}>
        <div className={classes.cardNumber}>{number}</div>
      </div>
      <div className={classes.cardContent}>
        <div className={classes.cardTitle}>{title}</div>
        <div className={classes.cardText}>{text}</div>
      </div>
    </div>
  );
}
